import type {AlarmsService} from '../../core/services/alarms.service';
import {UrlService} from '../../core/services/url.service';
import {AlarmMenuOptionId} from '../constants/menu-option-ids';
import {AlarmEvent} from '../models/alarm';
import {ObjectType} from '../models/object.model';
import {Option, OptionGroup} from '../models/option';
import {TileId} from '../models/tile';

export function mapActiveTileAlarms(events: AlarmEvent[]): Map<TileId, AlarmEvent> {
  const alarms = new Map<TileId, AlarmEvent>();

  for (const event of events) {
    if (event.ObjectType !== ObjectType.TILE) {
      continue;
    }

    const existing = alarms.get(event.ObjectID);

    if (!existing || (existing.AckTime && !event.AckTime)) {
      alarms.set(event.ObjectID, event);
    }
  }

  return alarms;
}

export function haveSameTileAlarms(a: Map<TileId, AlarmEvent>, b: Map<TileId, AlarmEvent>): boolean {
  if (a.size !== b.size) {
    return false;
  }

  for (const [tileId, event] of a) {
    const other = b.get(tileId);

    if (!other || other.UUID !== event.UUID || other.AckTime !== event.AckTime) {
      return false;
    }
  }

  return true;
}

export function makeAlarmMenuOptions(
  alarm: AlarmEvent,
  alarmsService: Pick<AlarmsService, 'acknowledgeEvent$' | 'clearEvent$'>,
  onCleared?: () => void,
): OptionGroup {
  const options: Option[] = [
    {
      id: AlarmMenuOptionId.VIEW_DETAILS,
      name: 'View Details',
      href: UrlService.makeAlarmEventDetailsUrl(alarm.UUID),
    },
    {
      id: AlarmMenuOptionId.CLEAR,
      name: 'Clear Alarm',
      callback: () => {
        alarmsService.clearEvent$(alarm.UUID).subscribe(() => onCleared?.());
      },
    },
  ];

  if (!alarm.AckTime) {
    options.splice(1, 0, {
      id: AlarmMenuOptionId.ACKNOWLEDGE,
      name: 'Acknowledge Alarm',
      callback: () => {
        alarmsService.acknowledgeEvent$(alarm.UUID).subscribe();
      },
    });
  }

  return new OptionGroup(options);
}
